import React, { ReactElement, useState, useCallback } from 'react';

import { useCart } from '~/lib/cart';
import { CartProps } from '~/types/cart';
import { cartContainsPlusDeal } from '~/lib/checkout/util';
import { CREDIT_COMPONENT_TYPES } from '~/constants/checkout';

// Direct import needed, otherwise "undefined" at runtime due to use in CREDIT_COMPONENT_DICT
import CreditsCollapsed from '~/components/checkout/CreditsCoupons/Credits/CreditsCollapsed';
import CreditsExpanded from '~/components/checkout/CreditsCoupons/Credits/CreditsExpanded';

interface CreditsProps {
  cart?: CartProps;
  type: CREDIT_COMPONENT_TYPES;
}

const CREDIT_COMPONENT_DICT = {
  [CREDIT_COMPONENT_TYPES.EXPANDED]: CreditsExpanded,
  [CREDIT_COMPONENT_TYPES.COLLAPSED]: CreditsCollapsed,
};

export default React.memo(function Credits({
  cart,
  type,
}: Readonly<CreditsProps>): ReactElement {
  const { toggleCredits } = useCart();
  const [isCreditsPending, setIsCreditsPending] = useState(false);
  const [error, setError] = useState<string>('');
  const isCreditsApplied = !!cart?.credit;

  const onClickToggle = useCallback(() => {
    if (error) setError('');
    setIsCreditsPending(true);

    let err = false;
    toggleCredits(!isCreditsApplied)
      .then(([, updatedCart]) => {
        // Something went wrong if we are missing cart data or nothing changed in the updated cart
        // Explicitly check for 1 undefined to avoid false positives
        err =
          !cart || !updatedCart || cart.use_credit === updatedCart.use_credit;
      })
      .catch(() => {
        err = true;
      })
      .finally(() => {
        err &&
          setError(
            `Unable to ${
              !isCreditsApplied ? 'add' : 'remove'
            } credits. Please try again.`,
          );
        setIsCreditsPending(false);
      });
  }, [cart, isCreditsApplied, error, toggleCredits]);

  if (
    !cart?.available_credit ||
    (cartContainsPlusDeal(cart) && cart?.cart_items?.length === 1)
  )
    return <></>;

  const CreditComponent = CREDIT_COMPONENT_DICT[type];

  return (
    <CreditComponent
      cart={cart}
      isCreditsPending={isCreditsPending}
      error={error}
      onClickToggle={onClickToggle}
    />
  );
});
